import { displayDecimalFormatted, getAccountCurrency } from "../../utils/systemSettings";

/** How the server reads a score: the values of `PerformanceStatus`. */
export const PerformanceStatus = { NO_DATA: 0, AT_RISK: 1, WATCH: 2, GOOD: 3 };

/**
 * What each status is called and the colours it is drawn in: the strong colour, the soft one behind it, and a fainter
 * tint and a line colour for surfaces that wear the colour of the status.
 */
export const STATUS_STYLE = {
  [PerformanceStatus.NO_DATA]: { label: "No data", color: "#8b8fa3", soft: "#f3f4f8", faint: "#fafafc", line: "#e3e5ec" },
  [PerformanceStatus.AT_RISK]: { label: "At risk", color: "#c62839", soft: "#fdecee", faint: "#fff7f8", line: "#f3c6cc" },
  [PerformanceStatus.WATCH]: { label: "Watch", color: "#a16207", soft: "#fff5d6", faint: "#fffbea", line: "#f1dc9c" },
  [PerformanceStatus.GOOD]: { label: "Good", color: "#1d8a4a", soft: "#e7f6ed", faint: "#f4fbf6", line: "#bfe3cc" },
};

/**
 * The verdict of the global performance index that Resource Insights wears: its colours, its name and its score, or
 * null when it should not colour anything. A score that rests on very little data is not given a verdict, so a week
 * with a single late task does not turn a resource red.
 */
export function performanceVerdict(global) {
  if (!global || global.score === null || global.score === undefined || global.lowData) return null;
  if (global.status === PerformanceStatus.NO_DATA) return null;
  return { ...STATUS_STYLE[global.status], score: global.score };
}

/** A score as shown: no decimal when it is whole, and a dash when there is none. */
export function formatScore(score) {
  if (score === null || score === undefined) return "–";
  return Number.isInteger(score) ? String(score) : score.toFixed(1);
}

/** A change in points as shown, with its sign: "+4.2", "−3" or "0". */
export function formatDelta(delta) {
  if (delta === null || delta === undefined) return null;
  if (delta === 0) return "0";
  const magnitude = Math.abs(delta);
  return `${delta > 0 ? "+" : "−"}${Number.isInteger(magnitude) ? magnitude : magnitude.toFixed(1)}`;
}

/**
 * The value of a figure of an index with its unit: "86 %", "3 days", "40 h", "0.92" or "5". A figure that is a
 * difference (`showSign`) says which way it goes: "+12 h", "−8 h" or "0 h".
 */
export function formatKpiValue(kpi) {
  if (kpi.value === null || kpi.value === undefined) return "–";

  const magnitude = Math.abs(kpi.value);
  const sign = kpi.value < 0 ? "−" : kpi.showSign && kpi.value > 0 ? "+" : "";
  const value = sign + (Number.isInteger(magnitude) ? String(magnitude) : magnitude.toFixed(kpi.unit === "ratio" ? 2 : 1));

  switch (kpi.unit) {
    case "%":
      return `${value} %`;
    case "days":
      return `${value} ${kpi.value === 1 ? "day" : "days"}`;
    case "h":
      return `${value} h`;
    default:
      return value;
  }
}

/** What each index measures and how its score is made, for the tooltip of its tile. */
export const INDEX_EXPLANATION = {
  global: "The mean of the indices below that could be measured, each weighted by its share. An index with no data does not count.",
  delivery: "How punctually the tasks that fell due in the period were finished. A late task loses credit in proportion to its delay over its own duration; critical tasks and milestones count double.",
  schedule: "How far the tasks under way are compared with where their dates say they should be, weighted by allocated hours. A task ahead of its forecast does not make up for one behind.",
  workload:
    "How well the hours forecast fit the resource's own capacity, which is their maximum weekly hours: utilization between 70 and 100 % is healthy, and days with more hours than the daily capacity lower the score. On each project the hours come from its tasks when they have any, otherwise from the cashflow forecast, otherwise from the weekly share.",
  timeTracking:
    "How closely the actual hours, which are the ones on validated timesheets, follow the hours forecast on the projects where time is entered (the cashflow forecast, or the weekly share for a month without one), and how promptly timesheets were handed in.",
};

/** The values of `ProjectType`. */
export const ProjectType = { FIXED_PRICE: 0, TIME_AND_MATERIALS: 1 };

export const PROJECT_TYPE_LABEL = {
  [ProjectType.FIXED_PRICE]: "Fixed price",
  [ProjectType.TIME_AND_MATERIALS]: "Time & materials",
};

/** The values of `BudgetStatus`, with what each is called and the colours it is drawn in. */
export const BUDGET_STATUS_STYLE = {
  0: { label: "Within budget", color: "#1d8a4a", soft: "#e7f6ed" },
  1: { label: "Over budget", color: "#c62839", soft: "#fdecee" },
};

/**
 * The values of `MarginStatus`, with what each is called and the colours it is drawn in. The margin is the gross
 * profit (GP). A GP that is not above the minimum is called "No GP" while the minimum is zero, which is when it can only
 * be a GP of exactly zero.
 */
export function marginStatusStyle(status, minimumMarginPercent) {
  switch (status) {
    case 1:
      return { label: "Loss", color: "#c62839", soft: "#fdecee" };
    case 2:
      return { label: minimumMarginPercent > 0 ? "Below minimum GP" : "No GP", color: "#a16207", soft: "#fff5d6" };
    case 3:
      return { label: "Profitable", color: "#1d8a4a", soft: "#e7f6ed" };
    default:
      return { label: "No forecast", color: "#8b8fa3", soft: "#f3f4f8" };
  }
}

/** Hours with their sign and at most two decimals: "+50 h", "−10 h" or "0 h". */
export function formatSignedHours(value) {
  if (value === null || value === undefined) return "–";
  if (value === 0) return "0 h";
  const magnitude = Math.abs(value);
  return `${value > 0 ? "+" : "−"}${Number.isInteger(magnitude) ? magnitude : magnitude.toFixed(2).replace(/0+$/, "")} h`;
}

/** A percentage with one decimal at most: "37.5 %" or "−1.6 %". */
export function formatPercent(value) {
  if (value === null || value === undefined) return "–";
  const text = Number.isInteger(value) ? String(Math.abs(value)) : Math.abs(value).toFixed(1);
  return `${value < 0 ? "−" : ""}${text} %`;
}

/** A change in percent with its sign: "+12.5 %", "−4 %" or "0 %". */
export function formatSignedPercent(value) {
  if (value === null || value === undefined) return "–";
  if (value === 0) return "0 %";
  return `${value > 0 ? "+" : ""}${formatPercent(value)}`;
}

/** An amount of money in the currency and number format of the account, without decimals. */
export function formatMoney(value) {
  if (value === null || value === undefined) return "–";

  let settings = {};
  try {
    settings = JSON.parse(localStorage.getItem("settings")) ?? {};
  } catch {
    settings = {};
  }

  const amount = Math.round(value);
  return `${amount < 0 ? "−" : ""}${getAccountCurrency(settings)} ${displayDecimalFormatted(settings, Math.abs(amount))}`;
}
